import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { computeObjectContainContentRect } from '@/lib/videoSubtitlePreview';
import { normalizedLayerToPixelRect } from '@/lib/subtitles/subtitleCoordinates';
import {
  dragSubtitleLayer,
  handlePositionStyle,
  normalizeRotationSigned,
  pointerAngleFromCenter,
  resizeSubtitleLayerFromHandle,
  rotationFromPointerDelta,
  SUBTITLE_RESIZE_HANDLES,
} from '@/lib/subtitles/subtitleTransform';
import { useRafThrottle } from '@/hooks/useRafThrottle';
import {
  hostOverlayPlaybackUrl,
  HOST_OVERLAY_LAYER_RESIZE_OPTIONS,
  LAYOUT_BROLL_LAYER_RESIZE_OPTIONS,
  isArtifactStickerClip,
  isBackgroundOverlayClip,
  isLayoutBrollClip,
  isTitleOverlayClip,
  normalizeHostOverlayLayer,
  normalizeHostOverlayOpacity,
  normalizeOverlayKind,
  OVERLAY_KIND_CARD,
} from '@/lib/videoEditorHostOverlays';
import { CardFrame, cardClipLocalTime } from '@/components/cardCarousel/CardFrame';
import { ArtifactSticker } from '@/components/reelsShorts/ArtifactSticker';
import { ReelsTitleFrame } from '@/components/reelsShorts/ReelsShortsTitleTemplates';
import HostOverlayOpacityBar from '@/components/hostOverlay/HostOverlayOpacityBar';

const HANDLE_HIT_CLASS =
  'absolute z-[60] rounded-sm border-2 border-white bg-amber-400 shadow-md hover:bg-amber-300 touch-none pointer-events-auto';
const HANDLE_SIZE = 18;
const HANDLE_INSET = -9;

/**
 * Draggable host/narrator photo on the preview frame (normalized layer coords).
 */
export default function HostOverlayPreview({
  stageRef,
  stageWidth,
  stageHeight,
  contentRect,
  aspectRatio,
  clip,
  isSelected = false,
  interactive = true,
  snapToGrid = false,
  onSelect,
  onLayerChange,
  onOpacityChange,
  onRevealInTimeline,
  onOpenDetails,
  timelineTime = 0,
  isPlaying = false,
}) {
  const [interacting, setInteracting] = useState(false);
  const [liveLayer, setLiveLayer] = useState(null);
  const [observedStage, setObservedStage] = useState({ w: 0, h: 0 });
  const liveLayerRef = useRef(null);
  const sessionRef = useRef(null);
  const frameScreenRectRef = useRef(null);

  const isBackground = isBackgroundOverlayClip(clip);
  const isCard = normalizeOverlayKind(clip?.overlayKind) === OVERLAY_KIND_CARD;
  const isArtifact = isArtifactStickerClip(clip);
  const isTitle = isTitleOverlayClip(clip);
  const isLayoutBroll = isLayoutBrollClip(clip);
  const layerBounds = isLayoutBroll ? LAYOUT_BROLL_LAYER_RESIZE_OPTIONS : null;
  const layerResizeOptions = isLayoutBroll
    ? { snap: snapToGrid, ...LAYOUT_BROLL_LAYER_RESIZE_OPTIONS }
    : isTitle
      ? {
        minWidth: 0.28,
        minHeight: 0.08,
        maxWidth: 0.98,
        maxHeight: 0.5,
        positionMode: 'free',
        snap: snapToGrid,
      }
      : { snap: snapToGrid, ...HOST_OVERLAY_LAYER_RESIZE_OPTIONS };

  const resolvedLayer = useMemo(
    () => normalizeHostOverlayLayer(clip?.layer, aspectRatio, layerBounds),
    [clip?.layer, aspectRatio, layerBounds],
  );
  const displayLayer = liveLayer || resolvedLayer;

  const imageSrc = useMemo(() => {
    const resolved = hostOverlayPlaybackUrl(clip);
    if (resolved) return resolved;
    const raw = String(clip?.imageUrl || clip?.image_url || '').trim();
    const s3 = String(clip?.image_s3_url || clip?.imageS3Url || '').trim();
    return raw || s3 || '';
  }, [clip]);
  const clipOpacity = normalizeHostOverlayOpacity(clip?.opacity);
  const borderRadius = isBackground || isArtifact || isTitle
    ? 0
    : Math.max(0, Math.min(0.5, Number(clip?.borderRadius) ?? 0.12));

  useLayoutEffect(() => {
    const stage = stageRef?.current;
    if (!stage) return undefined;
    const measure = () => {
      const bounds = stage.getBoundingClientRect();
      const w = Math.round(bounds.width);
      const h = Math.round(bounds.height);
      if (w < 1 || h < 1) return;
      setObservedStage((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [stageRef, clip?.id]);

  const measuredStage = useMemo(() => {
    const w = stageWidth > 0 ? stageWidth : observedStage.w;
    const h = stageHeight > 0 ? stageHeight : observedStage.h;
    const rect = contentRect?.width > 0 && contentRect?.height > 0
      ? contentRect
      : computeObjectContainContentRect(w, h, aspectRatio);
    return { stageWidth: w, stageHeight: h, contentRect: rect };
  }, [stageWidth, stageHeight, observedStage.w, observedStage.h, contentRect, aspectRatio]);

  const frameW = Math.max(1, measuredStage.contentRect?.width || measuredStage.stageWidth || 1);
  const frameH = Math.max(1, measuredStage.contentRect?.height || measuredStage.stageHeight || 1);
  const frameLeft = measuredStage.contentRect?.left ?? 0;
  const frameTop = measuredStage.contentRect?.top ?? 0;

  const updateFrameScreenRect = useCallback(() => {
    const stage = stageRef?.current;
    if (!stage) return null;
    const sr = stage.getBoundingClientRect();
    const cr = measuredStage.contentRect?.width
      ? measuredStage.contentRect
      : computeObjectContainContentRect(sr.width, sr.height, aspectRatio);
    const rect = {
      left: sr.left + (cr.left || 0),
      top: sr.top + (cr.top || 0),
      width: cr.width || sr.width,
      height: cr.height || sr.height,
    };
    frameScreenRectRef.current = rect;
    return rect;
  }, [stageRef, measuredStage.contentRect, aspectRatio]);

  const pixelRect = useMemo(
    () => normalizedLayerToPixelRect(displayLayer, frameW, frameH),
    [displayLayer, frameW, frameH],
  );

  const { schedule: scheduleLayerPreview, cancel: cancelLayerPreview } = useRafThrottle((next) => {
    liveLayerRef.current = next;
    setLiveLayer(next);
  });

  const endSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      window.removeEventListener('mousemove', session.onMove);
      window.removeEventListener('mouseup', session.onEnd);
      window.removeEventListener('touchmove', session.onTouchMove);
      window.removeEventListener('touchend', session.onEnd);
    }
    cancelLayerPreview();
    const final = liveLayerRef.current;
    liveLayerRef.current = null;
    setLiveLayer(null);
    if (final) onLayerChange?.(normalizeHostOverlayLayer(final, aspectRatio, layerBounds));
    setInteracting(false);
  }, [cancelLayerPreview, onLayerChange, aspectRatio, layerBounds]);

  const startSession = useCallback(
    (mode, handle, e) => {
      if (!interactive) return;
      e.preventDefault();
      e.stopPropagation();
      setInteracting(true);

      const pt = 'touches' in e ? e.touches[0] : e;
      const frameScreenRect = updateFrameScreenRect();
      if (!pt || !frameScreenRect || frameScreenRect.width < 8 || frameScreenRect.height < 8) {
        setInteracting(false);
        return;
      }

      const startLayer = { ...resolvedLayer };
      const startX = pt.clientX;
      const startY = pt.clientY;
      const resizeOpts = layerResizeOptions;

      let rotatePointerStart = null;
      let rotateValueStart = null;
      if (mode === 'rotate') {
        const pr = normalizedLayerToPixelRect(startLayer, frameW, frameH);
        const cx = frameScreenRect.left + pr.centerX;
        const cy = frameScreenRect.top + pr.centerY;
        rotatePointerStart = pointerAngleFromCenter(startX, startY, cx, cy) + 90;
        rotateValueStart = normalizeRotationSigned(startLayer.rotation || 0);
      }

      const onMove = (ev) => {
        const p = 'touches' in ev ? ev.touches[0] : ev;
        if (!p) return;
        const dx = (p.clientX - startX) / frameW;
        const dy = (p.clientY - startY) / frameH;
        let next = startLayer;

        if (mode === 'move') {
          next = dragSubtitleLayer(startLayer, dx, dy, aspectRatio, layerResizeOptions);
        } else if (mode === 'resize' && handle) {
          next = resizeSubtitleLayerFromHandle(
            handle,
            startLayer,
            dx,
            dy,
            aspectRatio,
            resizeOpts,
          );
        } else if (mode === 'rotate' && rotatePointerStart != null) {
          const pr = normalizedLayerToPixelRect(startLayer, frameW, frameH);
          const cx = frameScreenRect.left + pr.centerX;
          const cy = frameScreenRect.top + pr.centerY;
          const rot = rotationFromPointerDelta(
            p.clientX,
            p.clientY,
            cx,
            cy,
            rotatePointerStart,
            rotateValueStart,
          );
          next = { ...startLayer, rotation: rot };
        }
        scheduleLayerPreview(next);
      };

      const onEnd = () => endSession();
      const onTouchMove = (ev) => {
        ev.preventDefault();
        onMove(ev);
      };

      sessionRef.current = { onMove, onEnd, onTouchMove };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [
      interactive,
      isTitle,
      layerResizeOptions,
      resolvedLayer,
      frameW,
      frameH,
      aspectRatio,
      snapToGrid,
      scheduleLayerPreview,
      endSession,
      updateFrameScreenRect,
    ],
  );

  useLayoutEffect(() => {
    updateFrameScreenRect();
  }, [updateFrameScreenRect]);

  useEffect(() => () => {
    const session = sessionRef.current;
    if (!session) return;
    window.removeEventListener('mousemove', session.onMove);
    window.removeEventListener('mouseup', session.onEnd);
    window.removeEventListener('touchmove', session.onTouchMove);
    window.removeEventListener('touchend', session.onEnd);
    sessionRef.current = null;
  }, []);

  if (!clip) return null;
  if (!isCard && !isArtifact && !isTitle && !isLayoutBroll && !imageSrc) return null;

  const canEdit = interactive && isSelected && !isBackground;
  const canSelect = interactive && !isBackground;
  const boxCapturesPointer = canSelect && !isTitle;
  const showOpacityBar = canEdit && !isCard && !isTitle && Boolean(onOpacityChange);
  const rot = normalizeRotationSigned(displayLayer.rotation || 0);

  const handlePreviewMouseDown = (e) => {
    if (!interactive) return;
    if (e.target?.closest?.('[data-host-handle]')) return;
    if (!isSelected) {
      onSelect?.();
      return;
    }
    startSession('move', null, e);
  };

  const handlePreviewDoubleClick = (e) => {
    if (!interactive) return;
    if (e.target?.closest?.('[data-host-handle]')) return;
    if (e.target?.closest?.('[data-host-handle="opacity"]')) return;
    e.stopPropagation();
    e.preventDefault();
    endSession();
    if (onOpenDetails) {
      onOpenDetails(clip);
      return;
    }
    onRevealInTimeline?.(clip);
  };

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="host-overlay-preview"
    >
      <div
        className="absolute overflow-visible"
        style={{ left: frameLeft, top: frameTop, width: frameW, height: frameH }}
      >
        <div
          className={`absolute touch-none select-none overflow-visible ${boxCapturesPointer ? 'pointer-events-auto' : 'pointer-events-none'} ${canEdit && !isTitle ? 'cursor-move' : (boxCapturesPointer ? 'cursor-pointer' : '')}`}
          style={{
            left: pixelRect.left,
            top: pixelRect.top,
            width: isBackground || isTitle || isLayoutBroll ? pixelRect.width : Math.max(48, pixelRect.width),
            height: isBackground || isTitle || isLayoutBroll ? pixelRect.height : Math.max(48, pixelRect.height),
            transform: `rotate(${rot}deg)`,
            transformOrigin: 'center center',
          }}
          onMouseDown={interactive ? handlePreviewMouseDown : undefined}
          onDoubleClick={interactive && (onOpenDetails || onRevealInTimeline) ? handlePreviewDoubleClick : undefined}
          data-testid={`host-overlay-preview-box-${clip.id}`}
          title={canEdit
            ? 'Drag to move · handles to resize · cyan bar sets opacity · double-click for details'
            : interactive ? 'Click to select · double-click for details' : undefined}
        >
          <div
            className="absolute inset-0 overflow-visible"
            style={{
              borderRadius: isBackground || isCard || isArtifact || isTitle ? 0 : `${borderRadius * 100}%`,
              border: 'none',
              boxShadow: 'none',
              background: 'transparent',
              opacity: isCard ? 1 : clipOpacity,
            }}
          >
            {isCard ? (
              <CardFrame
                clip={clip}
                localTime={cardClipLocalTime(clip, timelineTime)}
                playing={isPlaying}
                selected={isSelected}
              />
            ) : isArtifact ? (
              <ArtifactSticker
                text={clip.displayText || clip.triggerText || clip.name || ''}
                imageUrl={imageSrc}
                mediaKind={clip.generatedMedia === 'video' ? 'video' : 'image'}
                animate={!interacting}
                motionStyle={clip.animationStyle}
              />
            ) : isTitle ? (
              <ReelsTitleFrame
                templateId={clip.titleTemplateId}
                line1={clip.titleLine1}
                line2={clip.titleLine2}
                style={clip.titleStyle}
              />
            ) : imageSrc ? (
            <img
              src={imageSrc}
              alt={clip.name || 'Host'}
              className="w-full h-full object-cover pointer-events-none select-none"
              draggable={false}
            />
            ) : isLayoutBroll ? (
              <div className="flex h-full w-full flex-col items-stretch justify-end bg-cyan-400/45 ring-2 ring-cyan-200/70">
                <p className="m-1.5 max-w-full rounded bg-black/70 px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-white">
                  B-ROLL pane
                </p>
                <p className="mx-1.5 mb-1.5 line-clamp-3 max-w-full rounded bg-black/55 px-2 py-1 text-center text-[10px] leading-snug text-white">
                  {clip.displayText || clip.generationPrompt || clip.name || 'Drag the handles to expand'}
                </p>
              </div>
            ) : null}
          </div>

          {showOpacityBar ? (
            <div
              className="absolute z-[70] pointer-events-auto"
              style={{
                top: 4,
                right: 4,
              }}
              data-host-handle="opacity"
            >
              <HostOverlayOpacityBar
                opacity={clipOpacity}
                onOpacityChange={onOpacityChange}
                testId={`host-overlay-opacity-bar-${clip.id}`}
              />
            </div>
          ) : null}

          {canEdit ? (
            <>
              <div
                className="absolute inset-0 rounded-sm pointer-events-none"
                style={{ border: '1.5px solid rgba(245,158,11,0.85)' }}
                aria-hidden
              />
              {SUBTITLE_RESIZE_HANDLES.map((h) => (
                <button
                  key={h}
                  type="button"
                  aria-label={`Resize ${h}`}
                  data-host-handle={h}
                  className={HANDLE_HIT_CLASS}
                  style={handlePositionStyle(h, HANDLE_INSET, HANDLE_SIZE)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startSession('resize', h, e);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startSession('resize', h, e);
                  }}
                />
              ))}
              <button
                type="button"
                aria-label="Rotate"
                data-host-handle="rotate"
                className={`${HANDLE_HIT_CLASS} flex items-center justify-center`}
                style={{
                  left: '50%',
                  top: -24,
                  marginLeft: -HANDLE_SIZE / 2,
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  cursor: 'grab',
                }}
                onMouseDown={(e) => startSession('rotate', null, e)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startSession('rotate', null, e);
                }}
              >
                <RotateCw className="w-3 h-3 text-white pointer-events-none" aria-hidden />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
